import React, { useEffect } from "react";
import AppTheme from "../../shared-theme/AppTheme";
import CssBaseline from "@mui/material/CssBaseline";
import ColorModeSelect from "../../shared-theme/ColorModeSelect";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import MuiCard from "@mui/material/Card";
import { styled } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { Checkbox, FormControlLabel, formHelperTextClasses, Grid, IconButton, InputAdornment, Snackbar } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { agentChangePassword, getAgentTwoFactorActive, getAgentTwoFactorInfo, verifyTwoFactCode } from "../../actions/agent";
import QRCode from "react-qr-code";
import Copyright from "../../internals/components/Copyright";
import { useNavigate } from "react-router";

const Card = styled(MuiCard)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	alignSelf: "center",
	width: "100%",
	padding: theme.spacing(4),
	gap: theme.spacing(2),
	margin: "auto",
	[theme.breakpoints.up("sm")]: {
		maxWidth: "450px",
	},
	boxShadow: "hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px",
	...theme.applyStyles("dark", {
		boxShadow: "hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px",
	}),
}));

const SignInContainer = styled(Stack)(({ theme }) => ({
	height: "calc((1 - var(--template-frame-height, 0)) * 100dvh)",
	minHeight: "100%",
	padding: theme.spacing(2),
	[theme.breakpoints.up("sm")]: {
		padding: theme.spacing(4),
	},
	"&::before": {
		content: '""',
		display: "block",
		position: "absolute",
		zIndex: -1,
		inset: 0,
		backgroundImage: "radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))",
		backgroundRepeat: "no-repeat",
		...theme.applyStyles("dark", {
			backgroundImage: "radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))",
		}),
	},
}));

const useStyles = makeStyles({
	snackbarStyleViaContentProps: {
		backgroundColor: "green",
	},
	snackbarStyleViaNestedContent: {
		backgroundColor: "lightgreen",
		color: "black",
	},
});

const TwoFactorAuth = (props) => {
	const { token, id, isAuthenticated, setIsAuthenticated } = props;
	const [verifyCodeError, setVerifyCodeError] = React.useState(false);
	const [verifyCodeErrorMessage, setVerifyCodeErrorMessage] = React.useState("");
	const [openSnack, setOpenSnack] = React.useState(false);
	const [showOptions, setShowOptions] = React.useState({
		showQr: false,
		showVerify: false,
		qrAuthString: "",
	});

	const classes = useStyles();
	const navigate = useNavigate();

	const changeTwoFactor = (e) => {
		e.preventDefault();
		getAgentTwoFactorActive(token, e.target.checked, (data) => {
			if (data) {
				let showq = data.data.twoFactorAuth;
				let showAct = true;
				if (!data.data.verify) {
					showAct = false;
				}
				setShowOptions({
					showQr: showq,
					showVerify: showAct,
					qrAuthString: data.data.strImageQR,
				});
			}
		});
	};

	const handleSubmit = (event) => {
		event.preventDefault();
		const data = new FormData(event.currentTarget);
		setVerifyCodeError(false);
		setVerifyCodeErrorMessage("");

		verifyTwoFactCode(token, data.get("pin"), (res) => {
			if (!res.error) {
				setOpenSnack(true);
				setInterval(() => {
					setOpenSnack(false);
				}, 4000);
			} else {
				setVerifyCodeError(true);
				setVerifyCodeErrorMessage(res.message);
			}
		});
	};
	const validateInputs = () => {
		const password = document.getElementById("password");
		const newpassword = document.getElementById("newpassword");
		const confirmpassword = document.getElementById("confirmpassword");
		let isValid = true;
		if (newpassword != confirmpassword) {
			verifyCodeError(true);
			setVerifyCodeErrorMessage("Wrong verify code");
			isValid = false;
		}

		return isValid;
	};

	useEffect(() => {
		getAgentTwoFactorInfo(token, (data) => {
			if (!data.error) {
				let showq = data.data.twoFactorAuth;
				let showAct = true;
				if (!data.data.verify) {
					showAct = false;
				}
				setShowOptions({
					showQr: showq,
					showVerify: showAct,
					qrAuthString: data.data.strImageQR,
				});
			} else {
				if (data.message.toString().includes("Token Expired")) {
					navigate("/logout");
				}
			}
		});
	}, []);

	return (
		<Box sx={{ width: "100%", maxWidth: { sm: "100%", md: "1700px" } }}>
			{/* cards */}
			<Typography component="h2" variant="h6" sx={{ mb: 2 }}>
				Change Password
			</Typography>
			<Grid container columns={12}>
				<Grid size={{ xs: 12, md: 12, lg: 12 }}>
					<Card variant="outlined">
						<Box
							component="form"
							onSubmit={handleSubmit}
							noValidate
							sx={{
								display: "flex",
								flexDirection: "column",
								width: "100%",
								gap: 2,
							}}
						>
							<FormControlLabel
								control={<Checkbox checked={showOptions.showVerify} name="twoFactorAuth" color="primary" />}
								label="Enable Two Factor Authentication using Google Authenticator"
								className={classes.checkTwoFactor}
								onChange={changeTwoFactor}
							/>
							{showOptions.showQr ? (
								<>
									<Box sx={{ alignContent: "center", alignItems: "center", textAlign: "center", width: "100%" }}>
										<img src={showOptions.qrAuthString} style={{}} />
									</Box>
									<FormControl>
										<FormLabel htmlFor="pin">Verification Code</FormLabel>
										<TextField
											error={verifyCodeError}
											helperText={verifyCodeErrorMessage}
											name="pin"
											placeholder="Code"
											type="number"
											id="pin"
											//autoComplete="current-password"
											required
											fullWidth
											variant="outlined"
											color={verifyCodeError ? "error" : "primary"}
										/>
									</FormControl>
									<Button type="submit" fullWidth variant="contained" onClick={validateInputs}>
										Verify
									</Button>
								</>
							) : null}
						</Box>
					</Card>
					<Snackbar
						anchorOrigin={{
							vertical: "center",
							horizontal: "center",
						}}
						ContentProps={{
							"aria-describedby": "message-id",
							className: classes.snackbarStyleViaContentProps,
						}}
						open={openSnack}
						onClose={() => setOpenSnack(false)}
						autoHideDuration={4000}
						message="Two factor changed"
						sx={{ backgroundColor: "green" }}
					/>
				</Grid>
			</Grid>
			<Copyright sx={{ my: 4 }} />
		</Box>
	);
};

export default TwoFactorAuth;
